// lib/home.ts
// 首页数据获取 hook + 打卡可视化元数据
//
// 设计（卡帕西视角）：
//   原版 loadHomeData 串行 7 次 IndexedDB 查询（cards → plans → logs → status → profile → emotions → mistakes），
//   但 7 个数据源互不依赖——全部应该 Promise.all 并行。
//   并行后首页数据加载从 7×RTT 降到 1×RTT（IndexedDB 事务在浏览器内仍串行执行，但调度并行）。
//
//   streakMeta 原本是组件内的 40 行 IIFE，纯函数特性 → 抽出为独立函数 getStreakMeta，
//   便于单测、复用、且使 page.tsx 渲染层保持"纯展示"。

import { useState, useEffect, useCallback, useRef } from "react";
import { getItem, listItems, countDueCards, listDueCards, listRecentItems } from "@/lib/storage/db";
import { KEY_PREFIXES } from "@/lib/types";
import type {
  LearningPlan,
  LearningPlanSummary,
  LearnLog,
  ScheduleItem,
  DailyStatus,
  PublicProfile,
  MistakeRecord,
  EmotionEntry,
  Achievement,
  HealthAlert,
  UserProfile,
  ReviewCard,
  CareerPath,
} from "@/lib/types";
import type { IconName } from "@/components/Icon";
import { getCareerPathById } from "@/lib/onboarding/career-paths";
import { chinaDateNow, chinaDateShift } from "@/lib/time";
import { getUnresolvedMistakes } from "@/lib/mistake-book";
import { autoFillTodayActualMinutes } from "@/lib/energy-collector";
import { maybeRetrain } from "@/lib/energy-regression";
import { maybeBuildProfile, getUserProfile } from "@/lib/ai/memory/user-profile";
import { checkAndNotify } from "@/lib/achievements";
import {
  planHealthCheck,
  shouldRunHealthCheck,
} from "@/lib/ai/plan-health";
import { getQualityReport } from "@/lib/ai/quality-tracker";
import { listPlanSummaries, migrateSummaries } from "@/lib/plan-summary";
import { buildStudyQueueFromData } from "@/lib/study-queue/build-study-queue";
import type { StudyTask } from "@/lib/study-queue/types";

// ============ 打卡可视化元数据 ============

export interface StreakMeta {
  color: string;
  emoji: string;
  sub: string;
  /** 是否断卡冲击态（用于动画/震动反馈） */
  shock: boolean;
}

/**
 * 根据当前连续天数 + 上次连续天数计算打卡可视化的颜色/emoji/文案
 * 纯函数，便于单测
 *
 * @param streak 当前连续打卡天数
 * @param lastStreak 上次断卡前的连续天数（streak=0 时用于显示"上次连续 X 天"）
 */
export function getStreakMeta(streak: number, lastStreak: number): StreakMeta {
  if (streak === 0) {
    if (lastStreak >= 3) {
      return {
        color: "bg-red-50 border-red-300 text-red-600",
        emoji: "heart",
        sub: `断卡！上次连续 ${lastStreak} 天`,
        shock: true,
      };
    }
    return {
      color: "bg-gray-50 border-gray-200 text-gray-500",
      emoji: "",
      sub: "今日未打卡",
      shock: false,
    };
  }
  if (streak >= 30)
    return {
      color: "bg-purple-50 border-purple-300 text-purple-700",
      emoji: "flame",
      sub: "满月达成！",
      shock: false,
    };
  if (streak >= 14)
    return {
      color: "bg-orange-50 border-orange-300 text-orange-700",
      emoji: "flame",
      sub: "两周连胜！",
      shock: false,
    };
  if (streak >= 7)
    return {
      color: "bg-orange-50 border-orange-300 text-orange-600",
      emoji: "flame",
      sub: "一周连胜！",
      shock: false,
    };
  if (streak >= 3)
    return {
      color: "bg-yellow-50 border-yellow-300 text-yellow-700",
      emoji: "star",
      sub: "保持节奏",
      shock: false,
    };
  return {
    color: "bg-blue-50 border-blue-300 text-blue-700",
    emoji: "leaf",
    sub: "开始打卡",
    shock: false,
  };
}

// ============ 首页数据状态 ============

export interface HomeData {
  dueCount: number;
  todayLearnCount: number;
  streak: number;
  /** 上一次连续天数（昨日或更早结束的连续段）—— 用于断卡视觉 */
  lastStreak: number;
  todaySchedule: Array<ScheduleItem & { planId: string; topic: string }>;
  heatmapData: Array<{ date: string; minutes: number }>;
  todayEnergy: number | null;
  latestPlan: { id: string; topic: string } | null;
  hasPlans: boolean | null;
  username: string;
  todayEmotions: EmotionEntry[];
  recentMistakes: MistakeRecord[];
  /** 后台检测到的新解锁成就（供首页顶部展示通知卡片） */
  newAchievements: Achievement[];
  /** 健康检查告警（首页顶部展示，可关闭 + 一键采纳） */
  healthAlerts: HealthAlert[];
  /**
   * 用户画像摘要（新增）：
   * - skillLevelCount：beginner/intermediate/advanced 三档节点数
   * - preferredSlot：偏好学习时段（如 "晚上"）
   * - averageSessionMinutes：平均专注时长
   * null 表示画像未构建或冷启动
   */
  userProfileSummary: {
    skillLevelCount: { beginner: number; intermediate: number; advanced: number };
    preferredSlot: string | null;
    averageSessionMinutes: number | null;
  } | null;
  /**
   * 最近 7 天能量趋势（新增）：[Monday, Tuesday, ..., Sunday] 的平均能量 1-5
   * 全为 null 表示无数据
   */
  energyTrend: Array<number | null>;
  /**
   * AI 质量摘要（新增）：今日 AI 调用数 + 整体采纳率
   * null 表示无 AI 调用记录
   */
  aiQualitySummary: {
    todayCalls: number;
    adoptionRate: number;
  } | null;
  /**
   * 今日学习队列（第 2 阶段：学习+复习合并）：
   * - 合并 plans 中的待学 schedule 项 + dueCards 中的待复习卡片
   * - 按智能优先级排序（review 紧迫度 + new 承接性 + 能量/多巴胺补偿）
   * - 空数组表示今日无待办（已完成或无计划）
   */
  studyQueue: StudyTask[];
  /**
   * 今日已完成学习/复习项数（第 2 阶段：KPI 三宫格第 2 格）
   * 来自 LearnLog 中今日 type 为 learn_complete / review_complete 的条数。
   * 0 表示今日尚未完成任何学习项。
   */
  todayCompletedCount: number;
  /**
   * 职业路径信息（V2 乔布斯视角重构）：
   * 从 onboarding 存储的 pathId 获取职业路径，计算进度和预计时间。
   * null 表示用户未通过新 onboarding 流程（旧用户兼容）。
   */
  careerPath: {
    title: string;
    icon: string;
    progress: number;
    weeksLeft: number;
    currentNodeTitle: string;
  } | null;
  /**
   * AI 教练每日洞察（V2）：
   * 基于昨日答题正确率、今日完成情况、连续天数生成有温度的一句话。
   * null 表示无足够数据生成洞察。
   */
  coachInsight: {
    tone: "encouraging" | "reminding" | "challenging" | "celebrating";
    message: string;
    icon: IconName;
  } | null;
}

// ============ 纯函数：从原始数据计算派生状态 ============
// 这些纯函数从原始查询结果计算派生状态，便于单测且不依赖 React。

/** 从 logs 计算连续打卡天数 + 上次连续天数 */
export function computeStreaks(
  logs: LearnLog[],
): { streak: number; lastStreak: number } {
  const logDates = new Set(logs.map((l) => l.date));

  let streak = 0;
  let checkDate = chinaDateNow();
  while (logDates.has(checkDate)) {
    streak++;
    checkDate = chinaDateShift(checkDate, -1);
  }

  let lastStreak = 0;
  if (streak === 0) {
    let yDate = chinaDateShift(chinaDateNow(), -1);
    while (logDates.has(yDate)) {
      lastStreak++;
      yDate = chinaDateShift(yDate, -1);
    }
  }
  return { streak, lastStreak };
}

/**
 * 派生今日已完成学习/复习项数（第 2 阶段：KPI 三宫格第 2 格）
 *
 * LearnLog 的 type 字段：
 *   - learn_complete / review_complete 表示今日完成的学习/复习动作
 *   - learn / review 表示学习/复习中（非完成）
 *   - question_view / question_favorite / focus_session 表示其他动作
 *
 * @param logs 最近 7 天 LearnLog 列表
 * @param today 今日日期（YYYY-MM-DD 中国时区）
 * @returns 今日完成的学习/复习项数
 */
export function deriveTodayCompletedCount(
  logs: LearnLog[],
  today: string,
): number {
  return logs.filter(
    (l) =>
      l.date === today &&
      (l.type === "learn_complete" || l.type === "review_complete"),
  ).length;
}

/**
 * 派生用户画像摘要（新增）：
 * - skillLevelCount：beginner/intermediate/advanced 三档节点数
 * - preferredSlot：偏好学习时段名（中文化）
 * - averageSessionMinutes：平均专注时长
 *
 * null 表示画像未构建
 */
export function deriveUserProfileSummary(
  profile: UserProfile | null,
): HomeData["userProfileSummary"] {
  if (!profile) return null;

  const skillLevelCount = { beginner: 0, intermediate: 0, advanced: 0 };
  for (const level of Object.values(profile.skillLevel ?? {})) {
    if (level === "beginner") skillLevelCount.beginner++;
    else if (level === "intermediate") skillLevelCount.intermediate++;
    else if (level === "advanced") skillLevelCount.advanced++;
  }

  const preferredSlot = profile.preferredTimeSlots?.[0]
    ? slotToChinese(profile.preferredTimeSlots[0])
    : null;

  return {
    skillLevelCount,
    preferredSlot,
    averageSessionMinutes: profile.averageSessionMinutes ?? null,
  };
}

/** 时段标识转中文（用于 UI 显示） */
function slotToChinese(slot: string): string {
  const map: Record<string, string> = {
    morning: "早上",
    afternoon: "下午",
    evening: "晚上",
    night: "深夜",
    "上午": "上午",
    "下午": "下午",
    "晚上": "晚上",
    "深夜": "深夜",
  };
  return map[slot] ?? slot;
}

/**
 * 派生最近 7 天能量趋势（新增）
 *
 * 简化实现：仅填充今日能量到对应 weekday 位置，其余为 null。
 * 完整实现需后台任务读取最近 7 天 DailyStatus；为保持 1×RTT，此处仅用 todayStatus。
 *
 * @returns 长度 7 的数组，索引 0 = 周一 ... 6 = 周日
 */
export function deriveEnergyTrend(
  todayStatus: DailyStatus | null,
): Array<number | null> {
  const trend: Array<number | null> = [null, null, null, null, null, null, null];
  if (todayStatus?.energy) {
    const jsDay = new Date().getDay();
    const mondayIndex = jsDay === 0 ? 6 : jsDay - 1;
    trend[mondayIndex] = todayStatus.energy;
  }
  return trend;
}

/**
 * 派生 AI 质量摘要（新增）
 *
 * - todayCalls：今日 AI 调用数
 * - adoptionRate：整体采纳率（按 calls 加权）
 *
 * null 表示今日无调用记录
 */
export function deriveAiQualitySummary(
  report: { totalCalls: number; scenes: Array<{ adoptionRate: number | null; calls: number }> },
): HomeData["aiQualitySummary"] {
  if (report.totalCalls === 0) return null;

  let totalWeight = 0;
  let weightedSum = 0;
  for (const scene of report.scenes) {
    if (scene.adoptionRate !== null) {
      weightedSum += scene.adoptionRate * scene.calls;
      totalWeight += scene.calls;
    }
  }
  const adoptionRate = totalWeight > 0 ? weightedSum / totalWeight : 0;

  return {
    todayCalls: report.totalCalls,
    adoptionRate: Math.round(adoptionRate * 100) / 100,
  };
}

/** 从 logs 计算最近 7 天热力图数据 */
export function computeHeatmap(
  logs: LearnLog[],
): Array<{ date: string; minutes: number }> {
  const out: Array<{ date: string; minutes: number }> = [];
  for (let i = 6; i >= 0; i--) {
    const date = chinaDateShift(chinaDateNow(), -i);
    const minutes = logs
      .filter((l) => l.date === date)
      .reduce((sum, l) => sum + (l.duration ?? 0), 0);
    out.push({ date, minutes });
  }
  return out;
}

/**
 * 派生职业路径信息（V2 乔布斯视角重构）
 *
 * 进度计算（卡帕西视角）：
 *   - 分子：plan.knowledgeTree 中 mastered === true 的节点数
 *   - 分母：plan.knowledgeTree.length（用户实际计划的节点数，与 CareerPath.nodes 解耦）
 *   - 这样 plan 与 CareerPath 不需要一一对应，preset 模板可灵活调整
 *
 * weeksLeft 计算：
 *   - 剩余总小时数 = CareerPath.nodes 总 estimatedHours × (1 - progress/100)
 *     （用进度比例换算剩余时间，避免"哪些节点已完成"的判断难题）
 *   - weeksLeft = ceil(剩余总小时数 / CareerPath.weeklyHours)
 *
 * currentNodeTitle：
 *   - 假设 CareerPath.nodes 是线性顺序，已完成数 = round(progress% × nodes.length)
 *   - 取第「已完成数」个节点（即下一个待学节点）的 title
 *   - 若全部完成，取最后一个节点的 title
 *
 * @param pathDef 职业路径定义（来自 CAREER_PATHS）
 * @param plan 用户的学习计划（含 knowledgeTree 用于 mastered 判断）
 * @returns 路径信息；任一前置条件不满足时返回 null
 */
export function deriveCareerPath(
  pathDef: CareerPath,
  plan: LearningPlan,
): HomeData["careerPath"] {
  // 2026-07-27 改动：进度统计从知识点维度改成题目维度
  // 旧设计：progress = masteredCount / totalNodes × 100
  //   痛点：一个节点 8 道题，用户答对 7 题，节点还没 mastered（差 1 题），进度显示 0%
  //   用户做了 7 次正确动作，进度条纹丝不动 → "我白学了"的失败感
  // 新设计：progress = understoodCount / totalQuestions × 100
  //   每答对一题，进度条 +1/N，即时反馈
  const totalQuestions = plan.questions.length;
  if (totalQuestions === 0) {
    // 无题目：进度 0，weeksLeft 用 pathDef.weeksEstimate 兜底
    return {
      title: pathDef.title,
      icon: pathDef.icon,
      progress: 0,
      weeksLeft: pathDef.weeksEstimate,
      currentNodeTitle: pathDef.nodes[0]?.title ?? pathDef.title,
    };
  }

  const understoodCount = plan.questions.filter((q) => q.understood === true).length;
  const progress = Math.round((understoodCount / totalQuestions) * 100);

  // 剩余总小时数 = path 总 hours × (1 - 进度比)
  // 进度比用题目维度（与 progress 一致）
  const totalHours = pathDef.nodes.reduce((sum, n) => sum + n.estimatedHours, 0);
  const remainingHours = totalHours * (1 - understoodCount / totalQuestions);
  const weeksLeft = pathDef.weeklyHours > 0
    ? Math.max(1, Math.ceil(remainingHours / pathDef.weeklyHours))
    : pathDef.weeksEstimate;

  // 当前节点：第一个还有未 understood 题目的节点（用户正在学的节点）
  // 旧设计用 masteredCount 作为索引，改成按题目维度后用"第一个未全部 understood 的节点"
  const currentNode = plan.knowledgeTree.find((node) => {
    const nodeQuestions = plan.questions.filter((q) => q.nodeId === node.id);
    return nodeQuestions.length > 0 && !nodeQuestions.every((q) => q.understood === true);
  });
  const currentNodeTitle = currentNode?.title ?? pathDef.title;

  return {
    title: pathDef.title,
    icon: pathDef.icon,
    progress,
    weeksLeft,
    currentNodeTitle,
  };
}

/**
 * 派生 AI 教练每日洞察（V2）
 *
 * 规则（按优先级，先命中先返回）：
 *   1. streak >= 7 → celebrating: "连续打卡 X 天，你正在建立习惯！"
 *   2. todayCompletedCount > 0 → challenging: "今天已完成 X 项，继续推进！"
 *   3. 默认 → encouraging: "每天进步一点点，离 offer 越来越近"
 *
 * 2026-07-27 删除："今天还没开始，从第一个知识点开始吧"（streak === 0 提醒）
 *   用户反馈：这个提示一直在（只要 streak === 0），变成噪音，没有意义。
 *   用户打开 app 就是为了学习，不需要被告知"你还没开始"。
 *   删除后 streak === 0 回退到默认鼓励语。
 *
 * @returns 洞察对象；输入异常时返回 null
 */
export function deriveCoachInsight(params: {
  streak: number;
  todayCompletedCount: number;
}): HomeData["coachInsight"] {
  const { streak, todayCompletedCount } = params;

  if (streak >= 7) {
    return {
      tone: "celebrating",
      message: `连续打卡 ${streak} 天，你正在建立习惯！`,
      icon: "star",
    };
  }
  if (todayCompletedCount > 0) {
    return {
      tone: "challenging",
      message: `今天已完成 ${todayCompletedCount} 项，继续推进！`,
      icon: "zap",
    };
  }
  return {
    tone: "encouraging",
    message: "每天进步一点点，离 offer 越来越近",
    icon: "sparkles",
  };
}

/** 从 plans 计算今日学习安排 + 待学数 + 最新 plan
 *  P1 优化：接受 LearningPlanSummary（含 schedule 字段），避免首页加载完整 plan 的 knowledgeTree/questions
 *
 *  2026-07-25 用户需求：已掌握/全部看懂的节点不进入今日清单
 *  通过 summary.nodeStates 派生字段过滤（旧 summary 缺此字段时回退为空对象，不过滤）
 */
export function computeTodaySchedule(plans: LearningPlanSummary[]): {
  todaySchedule: Array<ScheduleItem & { planId: string; topic: string }>;
  todayLearnCount: number;
  latestPlan: { id: string; topic: string } | null;
  hasPlans: boolean;
} {
  let todayLearn = 0;
  const todayItems: Array<ScheduleItem & { planId: string; topic: string }> = [];
  for (const plan of plans) {
    // 2026-07-25：已掌握（mastered）或全部看懂（allUnderstood）的节点不进入今日清单
    const nodeStates = plan.nodeStates ?? {};
    const today = (plan.schedule ?? []).filter(
      (s) =>
        s.day === 1 &&
        !s.completed &&
        !nodeStates[s.nodeId]?.mastered &&
        !nodeStates[s.nodeId]?.allUnderstood,
    );
    for (const s of today) {
      todayItems.push({ ...s, planId: plan.id, topic: plan.topic });
    }
    todayLearn += today.filter((s) => s.type === "learn").length;
  }

  let latestPlan: { id: string; topic: string } | null = null;
  if (plans.length > 0) {
    const sorted = [...plans].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
    latestPlan = { id: sorted[0].id, topic: sorted[0].topic };
  }

  return {
    todaySchedule: todayItems.slice(0, 5),
    todayLearnCount: todayLearn,
    latestPlan,
    hasPlans: plans.length > 0,
  };
}

// ============ 首页数据 hook ============

/**
 * onboarding 存储数据（key = "my:onboarding"）
 *
 * 写入位置：app/onboarding/page.tsx → dbSet("my:onboarding", {...})
 * 读取位置：useHomeData 内并行查询，用于派生 careerPath
 */
interface OnboardingData {
  pathId: string;
  planId: string;
  completedAt: string;
}

/**
 * 加载首页所有数据
 *
 * 性能：用 Promise.all 把 7 次 IndexedDB 查询并行化
 *   原版串行：cards → plans → logs → status → profile → emotions → mistakes ≈ 7×RTT
 *   并行版：1×RTT（Dexie 内部仍串行执行，但调度并行，省 await 切换开销）
 *
 * 数据源之间无依赖：cards/plans/logs/emotions/profile/status 走不同前缀，
 *   mistakes 走 getUnresolvedMistakes() 内部独立查询，均可并行。
 *
 * V2 新增：onboarding（"my:onboarding" key）加入 Promise.all 并行查询；
 *   若有 pathId，再串行读取完整 plan（含 knowledgeTree）以计算 careerPath。
 *   该串行读取仅在用户走过 onboarding 流程时发生，旧用户路径为 null 不触发。
 */
export function useHomeData(): HomeData & {
  reload: () => Promise<void>;
  isLoading: boolean;
} {
  const [data, setData] = useState<HomeData & { isLoading: boolean }>({
    dueCount: 0,
    todayLearnCount: 0,
    streak: 0,
    lastStreak: 0,
    todaySchedule: [],
    heatmapData: [],
    todayEnergy: null,
    latestPlan: null,
    hasPlans: null,
    username: "",
    todayEmotions: [],
    recentMistakes: [],
    newAchievements: [],
    healthAlerts: [],
    userProfileSummary: null,
    energyTrend: [null, null, null, null, null, null, null],
    aiQualitySummary: null,
    studyQueue: [],
    todayCompletedCount: 0,
    careerPath: null,
    coachInsight: null,
    // 2026-07-27 新增：首页加载状态
    // 初始 true（首次加载显示骨架屏），load 完成后 false
    // 后续 reload（visibilitychange/focus）不重新设 true，避免闪骨架屏（数据直接更新）
    isLoading: true,
  });

  // 上次 load 完成时间戳（用于窗口聚焦自动 reload 的节流）
  // 设计动机见下方 visibilitychange useEffect
  const lastLoadRef = useRef<number>(0);
  // 是否已完成首次加载（区分首次 vs reload，避免 reload 闪骨架屏）
  const firstLoadedRef = useRef<boolean>(false);

  const load = useCallback(async () => {
    const today = chinaDateNow();
    const todayStatusKey = KEY_PREFIXES.STATUS + today;

    // 2026-07-30 防御性修复（卡帕西视角）：
    //   旧版 load 主体无顶层 try/catch，任何 IO 抛错 → load reject →
    //   isLoading 永远 true → 骨架屏永远卡住（用户反馈"首屏一直骨架屏"）。
    //   解法：整个数据加载段用 try/catch 包裹，catch 中强制 setData
    //   isLoading=false，让用户至少看到降级空状态而非永远骨架屏。
    //   单个 IO 失败不阻断：Promise.all 内部用 allSettled 模式降级。
    try {
      // 新用户首次访问：注入 demo 数据（frontend 示例计划 + 几张复习卡 + 2 天学习日志）
      // 让新用户立刻看到"有内容"的产品状态，而非空荡荡的空白引导页
      // 幂等：shouldInjectDemo 内部已检查 demo plan 是否存在，重复调用不会叠加
      // 注入完成后继续走下面的并行加载，此时 listPlanSummaries 会读到 demo plan
      try {
        const { shouldInjectDemo, injectDemoData } = await import("@/lib/demo/preset-data");
        if (await shouldInjectDemo()) {
          await injectDemoData();
        }
      } catch {
        // demo 注入失败不影响首页加载（继续走空状态分支）
      }
      // 今日 0 点 ISO（用于 AI 质量统计的 since 过滤）
      const todayStartIso = new Date(`${today}T00:00:00+08:00`).toISOString();

      // 先迁移旧摘要（补齐缺失/过期 summary，含 schedule 字段），
      // 再并行加载首页数据。迁移为 no-op 时仅扫 key，开销极小。
      // 2026-07-30：migrateSummaries 失败不阻断首页加载（allSettled 模式已保护后续 IO）。
      //   旧数据缺 summary 时 computeTodaySchedule 会降级为空 schedule，不影响渲染。
      try {
        await migrateSummaries();
      } catch {
        // 迁移失败静默：summary 缺失走空状态，首页仍可加载
      }
      // P1 精准查询优化（替代全量加载）：
      //   - 卡片：countDueCards(now) 走 dueAt 索引精准计数，O(due) 而非 O(n)
      //     （首页只需要 dueCount，不需要卡片数据本身）
      //   - 计划：listItems<LearningPlan>(PLAN) 加载完整 plan（含 questions + knowledgeTree）
      //     2026-07-27 改动：学习队列改成题目维度，需要 questions 字段；
      //     旧版用 listPlanSummaries() 加载轻量 summary（无 questions）已不够用
      //   - 日志/情绪：listRecentItems(prefix, 7) 只查最近 7 天，走 updatedAt 索引
      // 第 2 阶段：新增 listDueCards 拉到期卡片数据（用于 studyQueue 渲染）
      // V2：新增 onboarding 并行读取（无 IO 依赖）
      // 2026-07-30：改用 Promise.allSettled 防止单个 IO reject 导致整体卡死。
      //   任何一个 IO 失败时降级为空数组/undefined，首页仍能渲染（数据缺失 ≠ 页面不可用）。
      const now = new Date();
      const settled = await Promise.allSettled([
        countDueCards(now),
        listItems<LearningPlan>(KEY_PREFIXES.PLAN),
        listPlanSummaries(),
        listRecentItems<LearnLog>(KEY_PREFIXES.LEARN_LOG, 7),
        getItem<DailyStatus>(todayStatusKey),
        getItem<PublicProfile>("my:profile"),
        listRecentItems<EmotionEntry>(KEY_PREFIXES.EMOTION, 7),
        getUnresolvedMistakes(),
        getUserProfile(),
        getQualityReport(todayStartIso),
        listDueCards<ReviewCard>(now, 50),
        getItem<OnboardingData>("my:onboarding"),
      ]);
      // 解包 allSettled 结果：rejected 的降级为空值
      // 类型断言安全：fulfilled.value 类型与原 Promise 一致
      const v = <T,>(r: PromiseSettledResult<T>, fallback: T): T =>
        r.status === "fulfilled" ? r.value : fallback;
      const dueCount = v(settled[0], 0);
      const fullPlans = v(settled[1], [] as LearningPlan[]);
      const summaries = v(settled[2], [] as LearningPlanSummary[]);
      const logs = v(settled[3], [] as LearnLog[]);
      const todayStatus = v(settled[4], undefined as DailyStatus | undefined);
      const profile = v(settled[5], undefined as PublicProfile | undefined);
      const emotions = v(settled[6], [] as EmotionEntry[]);
      const mistakes = v(settled[7], [] as MistakeRecord[]);
      const userProfile = v(settled[8], null);
      const qualityReport = v(
        settled[9],
        { totalCalls: 0, scenes: [] } as { totalCalls: number; scenes: Array<{ adoptionRate: number | null; calls: number }> },
      );
      const dueCards = v(settled[10], [] as ReviewCard[]);
      const onboarding = v(settled[11], undefined as OnboardingData | undefined);

    // summaries 用于 computeTodaySchedule（旧 API，仍需要 summary 格式）
    // fullPlans 用于 buildStudyQueueFromData（题目维度）+ deriveCareerPath（按题目统计进度）
    const plans = summaries;

    // 内存派生计算（无 IO）
    const { streak, lastStreak } = computeStreaks(logs);
    const heatmapData = computeHeatmap(logs);
    const todayCompletedCount = deriveTodayCompletedCount(logs, today);
    const {
      todaySchedule,
      todayLearnCount,
      latestPlan,
      hasPlans,
    } = computeTodaySchedule(plans);

    // 第 2 阶段：构建今日学习队列（合并 plans 待学 + dueCards 待复习，按 priority 排序）
    // 排序上下文：用今日能量 + 今日多巴胺状态（来自 todayStatus）
    const studyQueueContext = {
      energy: todayStatus?.energy ?? 3,
      dopamine: todayStatus?.dopamineTrigger ?? ("无" as const),
    };
    const studyQueue = buildStudyQueueFromData(fullPlans, dueCards, {
      date: today,
      context: studyQueueContext,
      now,
    });

    // 派生：用户画像摘要（从 UserProfile 计算三档节点数）
    const userProfileSummary = deriveUserProfileSummary(userProfile);

    // 派生：最近 7 天能量趋势（从 DailyStatus 列表计算）
    // 注：这里复用 logs 的 date 信息；为避免新增 IO，从 logs 的 date 集合 + 已加载的 todayStatus 派生
    // 完整实现需要读取最近 7 天的 DailyStatus；为保持 1×RTT，此处仅用 todayStatus，
    // 后续可通过后台任务或扩展查询补全历史数据
    const energyTrend = deriveEnergyTrend(todayStatus ?? null);

    // 派生：AI 质量摘要（从 QualityReport 计算今日调用数 + 整体采纳率）
    const aiQualitySummary = deriveAiQualitySummary(qualityReport);

    // V2 派生：职业路径信息（仅当用户走过 onboarding 流程时计算）
    // 2026-07-27 优化：fullPlans 已在上方并行加载，直接从中查找 onboarding.planId，
    // 不再串行 getItem（减少 1 次 IO，首页加载更快）
    let careerPath: HomeData["careerPath"] = null;
    if (onboarding?.pathId) {
      const pathDef = getCareerPathById(onboarding.pathId);
      if (pathDef && onboarding.planId) {
        const plan = fullPlans.find((p) => p.id === onboarding.planId);
        if (plan) {
          careerPath = deriveCareerPath(pathDef, plan);
        }
      }
    }

    // V2 派生：AI 教练每日洞察（基于 streak + todayCompletedCount）
    const coachInsight = deriveCoachInsight({ streak, todayCompletedCount });

    setData({
      dueCount,
      todayLearnCount,
      streak,
      lastStreak,
      todaySchedule,
      heatmapData,
      todayEnergy: todayStatus?.energy ?? null,
      latestPlan,
      hasPlans,
      username: profile?.username ?? "",
      todayEmotions: emotions.filter((e) => e.date === today),
      recentMistakes: mistakes.slice(0, 3),
      newAchievements: [],
      healthAlerts: [],
      userProfileSummary,
      energyTrend,
      aiQualitySummary,
      studyQueue,
      todayCompletedCount,
      careerPath,
      coachInsight,
      // 2026-07-27：首次加载完成后设 false，后续 reload 直接更新数据不闪骨架屏
      isLoading: false,
    });
    firstLoadedRef.current = true;

    // 后台维护任务：不阻塞 UI，失败静默
    // - autoFillTodayActualMinutes: 自动回填今日 actualMinutes
    //   修复"模型永远无法训练"的冷启动问题（Issue 4）
    // - maybeRetrain: 检查是否需要重训练能量回归模型
    // - maybeBuildProfile: 懒构建/刷新用户画像（24h TTL）
    // - checkAndNotify: 成就检测 + 通知（新成就存入 state 供 UI 展示）
    // - maybeRunHealthCheck: 计划健康检查（当日只跑一次，HealthAlert 存入 state 供 UI 展示）
    void Promise.allSettled([
      autoFillTodayActualMinutes(),
      maybeRetrain(),
      maybeBuildProfile(),
      checkAndNotify(),
      maybeRunHealthCheck(today),
    ])
      .then((results) => {
        // checkAndNotify 是第 4 个；fulfilled 时取其返回的新成就
        const achievementResult = results[3];
        const newAchievements =
          achievementResult.status === "fulfilled" ? achievementResult.value : [];
        if (newAchievements.length > 0) {
          setData((prev) => ({ ...prev, newAchievements }));
        }
        // maybeRunHealthCheck 是第 5 个；fulfilled 时取其返回的 HealthAlert[]
        const healthResult = results[4];
        const healthAlerts =
          healthResult.status === "fulfilled" ? healthResult.value : [];
        if (healthAlerts.length > 0) {
          setData((prev) => ({ ...prev, healthAlerts }));
        }
      })
      .catch(() => {
        // 维护任务失败不影响首页加载
      });
    } catch (err) {
      // 2026-07-30 防御性兜底：
      //   任何未预期的异常（migrateSummaries 抛错、派生计算崩溃等）
      //   都必须让 isLoading=false，否则骨架屏永远卡住。
      //   降级为"空状态 + 错误提示"，用户至少能看到了解发生了什么。
      console.error("[useHomeData] load 失败，降级为空状态：", err);
      setData((prev) => ({
        ...prev,
        isLoading: false,
        hasPlans: prev.hasPlans, // 保留已读到的状态（reload 场景）
      }));
    }
  }, []);

  // mount 时首次加载
  useEffect(() => {
    // 2026-07-30：load 内部已有 try/catch，不会 reject。
    //   但保留 .catch 作为双保险，防止未来重构引入未捕获异常。
    void load()
      .catch((err) => {
        console.error("[useHomeData] mount load 未捕获：", err);
        setData((prev) => ({ ...prev, isLoading: false }));
      })
      .finally(() => {
        lastLoadRef.current = Date.now();
      });
  }, [load]);

  // 窗口聚焦/可见性变化时自动刷新（2026-07-27 修复）
  // 设计动机（用户反馈"训练页学会了，回首页 PathProgressBar 进度不更新 + 已 mastered 节点仍在今日清单"）：
  //   - useHomeData 只在 mount 时调 load，无自动刷新机制
  //   - 用户从训练页/计划详情页回首页时，首页 hook 不重新加载，显示旧数据
  //   - 根因：useHomeData 是 React state，路由切换不会重新触发 hook（首页组件可能在 App Router 缓存中）
  //   - 解法：监听 visibilitychange + focus，窗口聚焦时自动 reload
  // 节流：距离上次 load > 3s 才允许触发，避免短时间多次刷新（如切标签页快速来回）
  // 覆盖场景：
  //   - 训练页中途退出回首页 → 首页 reload → PathProgressBar 更新 + 今日清单过滤已 mastered 节点
  //   - 计划详情页标记 mastered 后回首页 → 同上
  //   - 任何修改 plan 的页面回首页 → 同上
  useEffect(() => {
    if (typeof window === "undefined") return; // SSR 安全
    const RELOAD_THROTTLE_MS = 3000;
    const triggerReload = () => {
      if (Date.now() - lastLoadRef.current < RELOAD_THROTTLE_MS) return;
      lastLoadRef.current = Date.now();
      void load();
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        triggerReload();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", triggerReload);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", triggerReload);
    };
  }, [load]);

  return { ...data, reload: load, isLoading: data.isLoading };
}

// ============ 内部工具：后台任务包装 ============

/**
 * 计划健康检查包装：当日只跑一次
 * - shouldRunHealthCheck 返回 true → 调 planHealthCheck() 返回 HealthAlert[]
 * - shouldRunHealthCheck 返回 false（当日已跑过）→ 返回空数组
 * - 任何异常吞掉返回空数组（与 Promise.allSettled 失败静默协议一致）
 */
async function maybeRunHealthCheck(today: string): Promise<HealthAlert[]> {
  try {
    const shouldRun = await shouldRunHealthCheck(today);
    if (!shouldRun) return [];
    return await planHealthCheck();
  } catch {
    return [];
  }
}
